import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * Fatih Emre Eryiğit — a personal contact page.
 *
 * Asked for 2026-09-15 with the address written out in full
 * (`/en/about/team/fatih-emre-eryigit`), so that is the slug — the link is
 * presumably already being handed out. The slug is the ASCII fold of the name,
 * which is also what the request wrote.
 *
 * The three facts supplied are the whole of the content: name, mobile, email.
 * No role, no portrait and no biography were given, and inventing a job title
 * for a named colleague is the one thing a page like this must not do — so the
 * page says what it knows and carries a `check` asking for the rest.
 *
 * `tel:` is the number with no spaces and the country code kept, which is what
 * a phone dials; the spaced form is what the reader sees.
 *
 * Asked for 2026-09-15: the team contact pages should read as one set, so this
 * page follows the shared shape — a centred name, a `features` grid whose card
 * title *is* the address or the number, and the same closing band back to the
 * firm. The eyebrow stays "Digitopia" rather than a role, because no role was
 * given; the `check` below is still the one asking for it.
 */
export const route: RouteDef = {
  id: 'teamFatihEmreEryigit',
  path: 'about/team/fatih-emre-eryigit',
  // Asked for 2026-09-15 for the other contact cards and applied here for the
  // same reason: they are handed out as QR codes and links, so nothing on the
  // site should lead to them — out of the sitemap and out of search results.
  // The URL itself still works.
  robots: 'noindex',
  priority: 0.5,
}

const EMAIL = 'fatih.eryigit@digitopia.ai'
const PHONE_DISPLAY = '+90 553 672 33 83'
const PHONE_DIAL = 'tel:+905536723383'
// Asked for 2026-09-17, for every team page: a button that saves the details
// straight into the reader's phone. The file is a static vCard in
// `public/vcf/`, named after this page's slug — see that folder's README.
const VCARD = '/vcf/fatih-emre-eryigit.vcf'

export const teamFatihEmreEryigit: WrittenPage = {
  navLabel: 'Fatih Emre Eryiğit',
  title: 'Fatih Emre Eryiğit',
  description:
    'Contact Fatih Emre Eryiğit at Digitopia: fatih.eryigit@digitopia.ai, +90 553 672 33 83.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Digitopia',
      headline: 'Fatih Emre Eryiğit',
      align: 'center',
      ctas: [
        { label: 'Email', href: `mailto:${EMAIL}` },
        { label: 'Call', href: PHONE_DIAL },
        { label: 'Save contact', href: VCARD },
      ],
      check:
        'No role, title or portrait was supplied for Fatih Emre Eryiğit, so the page ' +
        'carries his name and his contact details and nothing else. Send the job title ' +
        '(and a portrait, and a LinkedIn profile if it should be linked) and this ' +
        'becomes a proper profile. Also confirm that a personal mobile number is meant ' +
        'to be public on digitopia.ai.',
    },
    {
      kind: 'features',
      heading: 'Get in touch',
      columns: 2,
      items: [
        {
          title: EMAIL,
          note: 'Email',
          href: `mailto:${EMAIL}`,
        },
        {
          title: PHONE_DISPLAY,
          note: 'Türkiye',
          href: PHONE_DIAL,
        },
      ],
    },
    {
      kind: 'cta',
      heading: 'Rather start with the firm than a person?',
      sub: 'The contact form reaches the whole team, and About Us says who else you would be working with.',
      tone: 'band',
      ctas: [
        { label: 'Contact Digitopia', href: LINKS.contact },
        { label: 'About Digitopia', href: LINKS.about },
      ],
    },
  ],
}
